import { useEffect, useMemo, useState, type CSSProperties } from 'react';

import {
  benchmarkAgainstPublished,
  money,
  sizeBandOf,
  type PeerBenchmark,
  type PublishedPeerStats,
} from '@kilat/shared';

import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';
import { peerBenchmarksRepo } from '../lib/repositories/peer-benchmarks-repo.js';

/**
 * "How you compare" — peer benchmarking for operators (gap G24).
 *
 * Nothing in the product told an operator whether they were doing well. They
 * watched their own numbers move and had no idea whether that was good. A
 * visible, closeable gap — "carwashes your size are around RM 31" — is the
 * strongest operator motivator we have.
 *
 * It is also the only place one tenant's data informs what another sees, so the
 * card is built around what it must NOT do:
 *
 *   - It reads ONE published document holding a rounded median and a count.
 *     Peer figures never reach the browser, so no amount of devtools poking
 *     recovers them.
 *   - It re-checks the anonymity floor itself rather than trusting that
 *     document, and renders an explanation instead of a comparison when the
 *     cohort is too small.
 *   - It says, on the card, that nobody is named and nobody sees this
 *     operator's numbers. A comparison feature that does not explain its own
 *     privacy is one an operator is right to distrust.
 *
 * Opt-in is reciprocal and stated plainly: you see the comparison because you
 * are part of it.
 */

interface PeerBenchmarkCardProps {
  readonly platformBrandId: string;
  /** This tenant's average ticket over the same window, in sen. */
  readonly ownAvgTicketSen: number;
  /** Outlets they run — decides which cohort they are measured against. */
  readonly outletCount: number;
  /** Whether they participate. Non-participants see the invitation, not a comparison. */
  readonly optedIn: boolean;
  /** Called when the operator turns comparisons on. */
  readonly onOptIn?: () => void;
}

/** Matches the publisher's rounding for this metric: RM 1. */
const ROUND_TO_SEN = 100;

export function PeerBenchmarkCard({
  platformBrandId,
  ownAvgTicketSen,
  outletCount,
  optedIn,
  onOptIn,
}: PeerBenchmarkCardProps) {
  const { t } = useI18n();
  const [published, setPublished] = useState<PublishedPeerStats | null>(null);
  const [loading, setLoading] = useState(true);

  const sizeBand = useMemo(() => sizeBandOf(outletCount), [outletCount]);

  useEffect(() => {
    let cancelled = false;
    // A non-participant contributes nothing and receives nothing, so there is
    // no read to make.
    if (!optedIn) {
      setLoading(false);
      return;
    }
    setLoading(true);
    peerBenchmarksRepo
      .get(platformBrandId, 'avgTicketSen', sizeBand)
      .then((doc) => {
        if (cancelled) return;
        setPublished(doc ? { peerMedian: doc.peerMedian, cohortSize: doc.cohortSize } : null);
      })
      .catch((e: unknown) => {
        logger.error('peer benchmark load failed', { error: String(e) });
        if (!cancelled) setPublished(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [platformBrandId, sizeBand, optedIn]);

  const result: PeerBenchmark = useMemo(
    () =>
      benchmarkAgainstPublished({
        ownValue: ownAvgTicketSen,
        published,
        optedIn,
        roundTo: ROUND_TO_SEN,
      }),
    [ownAvgTicketSen, published, optedIn],
  );

  if (loading) {
    return (
      <section style={cardStyle} aria-busy>
        <h2 style={titleStyle}>{t('dashboard.benchmark.title')}</h2>
        <span style={srOnly}>{t('dashboard.benchmark.loading')}</span>
        <div aria-hidden className="k-skeleton" style={{ height: 64, borderRadius: 12 }} />
      </section>
    );
  }

  return (
    <section style={cardStyle}>
      <h2 style={titleStyle}>{t('dashboard.benchmark.title')}</h2>

      {result.kind === 'suppressed' && result.reason === 'not_opted_in' && (
        <div style={{ display: 'grid', gap: 12 }}>
          <h3 style={subTitleStyle}>{t('dashboard.benchmark.optInHeading')}</h3>
          <p style={bodyStyle}>{t('dashboard.benchmark.optInBody')}</p>
          {onOptIn ? (
            <button type="button" style={ctaStyle} onClick={onOptIn}>
              {t('dashboard.benchmark.optInCta')}
            </button>
          ) : null}
        </div>
      )}

      {result.kind === 'suppressed' && result.reason === 'too_few_peers' && (
        <div style={{ display: 'grid', gap: 8 }}>
          <h3 style={subTitleStyle}>{t('dashboard.benchmark.tooFewHeading')}</h3>
          {/* Says WHY rather than "unavailable": the silence is the feature
              working, and an operator who understands that trusts the number
              when it does appear. */}
          <p style={bodyStyle}>{t('dashboard.benchmark.tooFewBody')}</p>
        </div>
      )}

      {result.kind === 'suppressed' && result.reason === 'no_own_data' && (
        <div style={{ display: 'grid', gap: 8 }}>
          <h3 style={subTitleStyle}>{t('dashboard.benchmark.noDataHeading')}</h3>
          <p style={bodyStyle}>{t('dashboard.benchmark.noDataBody')}</p>
        </div>
      )}

      {result.kind === 'ranked' && (
        <div style={{ display: 'grid', gap: 8 }}>
          <p style={metricStyle}>
            {t('dashboard.benchmark.metricAvgTicket')} · {money.format(result.ownValue)}
          </p>
          <p style={bodyStyle}>
            {result.band === 'ahead' && t('dashboard.benchmark.ahead')}
            {result.band === 'typical' && t('dashboard.benchmark.typical')}
            {result.band === 'behind' &&
              t('dashboard.benchmark.behind', {
                median: money.format(result.peerMedian),
                gap: money.format(result.gapToMedian),
              })}
          </p>
          {/* The privacy promise sits on the card, not in a settings page
              nobody opens. */}
          <p style={footnoteStyle}>
            {t('dashboard.benchmark.cohort', { count: result.cohortSize })}
          </p>
        </div>
      )}
    </section>
  );
}

const srOnly: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const cardStyle: CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  boxShadow: 'var(--shadow-1)',
  display: 'grid',
  gap: 'var(--space-3)',
};

const titleStyle: CSSProperties = { margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 };
const subTitleStyle: CSSProperties = { margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 };
const bodyStyle: CSSProperties = { margin: 0, color: 'var(--color-on-surface)' };
const metricStyle: CSSProperties = { margin: 0, fontSize: 'var(--text-h2)', fontWeight: 700 };
const footnoteStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};
const ctaStyle: CSSProperties = {
  minHeight: 'var(--touch-target)',
  padding: '0 var(--space-5)',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  fontWeight: 600,
  fontSize: 'var(--text-body)',
  cursor: 'pointer',
  justifySelf: 'start',
};
